/**
 * Para qual funil vai a oportunidade que nasce de cada número ou canal.
 *
 * ── O que isto decide, e o que não decide ────────────────────────────────
 *
 * Decide ONDE nasce a oportunidade que a regra automática cria quando uma
 * conversa chega. Não decide SE ela nasce: isso continua na regra de
 * Configurações (janela, etapas abertas).
 *
 * Caixa sem regra vai para o funil padrão, que é a rede: nenhuma conversa
 * fica sem destino por falta de configuração.
 *
 * ── Uma caixa, um funil ──────────────────────────────────────────────────
 *
 * Um funil recebe quantas caixas quiser; cada caixa vai para um funil só. Duas
 * regras para o mesmo número obrigariam a escolher entre elas, e "depende" é
 * o que o roteamento existe para evitar.
 */

import { z } from 'zod';
import { InstanteSchema } from './valores.js';

export const RegraDeRoteamentoSchema = z
  .object({
    /** A chave do funil (VENDAS, POSVENDA), a mesma das rotas de funil. */
    funil: z.string().min(1),
    /** Em que etapa nasce. `null` é a primeira do funil. */
    etapa_inicial: z.string().min(1).nullable(),
    /**
     * As automações do número (056). Valem quando a regra CRIA a oportunidade;
     * a que já existia fica como está.
     *
     * `responsavel_usuario_id`: dono da oportunidade e da conversa.
     * `etiqueta`: acrescentada às da conversa, nunca no lugar delas.
     * `tarefa_em_horas`: cria a tarefa de primeiro contato com esse prazo.
     */
    responsavel_usuario_id: z.string().uuid().nullable(),
    etiqueta: z.string().min(1).nullable(),
    tarefa_em_horas: z.number().int().min(1).max(720).nullable(),
    atualizada_em: InstanteSchema,
  })
  .strict();

export type RegraDeRoteamento = z.infer<typeof RegraDeRoteamentoSchema>;

export const CaixaRoteadaSchema = z
  .object({
    /** O id da caixa no motor de conversa: um número de WhatsApp, uma conta. */
    caixa: z.string().min(1),
    nome: z.string(),
    /** `whatsapp_cloud`, `instagram`, `telegram`... */
    canal: z.string(),
    /** `null` é caixa sem regra: vai para o funil padrão. */
    regra: RegraDeRoteamentoSchema.nullable(),
  })
  .strict();

export type CaixaRoteada = z.infer<typeof CaixaRoteadaSchema>;

export const RoteamentoSchema = z
  .object({
    itens: z.array(CaixaRoteadaSchema),
  })
  .strict();

export type Roteamento = z.infer<typeof RoteamentoSchema>;

export const DefinirRoteamentoSchema = z
  .object({
    funil: z.string().trim().min(1),
    etapa_inicial: z.string().trim().min(1).nullable().default(null),
    responsavel_usuario_id: z.string().uuid().nullable().default(null),
    etiqueta: z.string().trim().min(1).max(80).nullable().default(null),
    tarefa_em_horas: z.number().int().min(1).max(720).nullable().default(null),
  })
  .strict();

export type DefinirRoteamento = z.infer<typeof DefinirRoteamentoSchema>;
